/**
 * Round splash overlay — pop "ROUND N" / "FINAL ROUND" briefly between rounds
 * so viewers see the transition. Adapted from WhatIs.
 */

const STYLES = `
  @keyframes an-splash-in {
    0%   { opacity: 0; transform: translate(-50%, calc(-50% - 12px)) scale(0.7); }
    18%  { opacity: 1; transform: translate(-50%, -50%) scale(1.08); }
    45%  { opacity: 1; transform: translate(-50%, -50%) scale(1.0); }
    78%  { opacity: 0.95; transform: translate(-50%, calc(-50% - 220px)) scale(0.7); }
    100% { opacity: 0; transform: translate(-50%, calc(-50% - 320px)) scale(0.6); }
  }
  @keyframes an-splash-shimmer {
    0%, 100% { text-shadow: 0 6px 28px rgba(0,0,0,0.7), 0 0 30px rgba(255,196,61,0.5); }
    50%      { text-shadow: 0 6px 28px rgba(0,0,0,0.7), 0 0 80px rgba(255,196,61,1.0); }
  }
  .an-splash {
    position: absolute;
    /* Positioned in the upper-third so the splash doesn't obscure the
       tile-stage at center. Floats up-and-out as it fades. */
    top: 28%;
    left: 750px;
    transform: translate(-50%, -50%) scale(0.7);
    z-index: 90;
    pointer-events: none;
    font-family: 'Oswald','Bebas Neue',Impact,sans-serif;
    text-align: center;
    padding: 20px 56px 24px 56px;
    background: linear-gradient(180deg, rgba(6,12,233,0.86) 0%, rgba(3,4,40,0.92) 100%);
    border: 4px solid var(--sg-primary, #FFC43D);
    border-radius: 18px;
    box-shadow: 0 12px 60px rgba(0,0,0,0.7), 0 0 80px rgba(255,196,61,0.4);
    color: #fff;
    animation: an-splash-in 1700ms cubic-bezier(.18,.85,.4,1.0) both;
  }
  .an-splash-title {
    font-size: 96px;
    line-height: 0.95;
    font-weight: 700;
    color: var(--sg-primary, #FFC43D);
    letter-spacing: 0.06em;
    animation: an-splash-shimmer 1.4s ease-in-out infinite;
  }
  .an-splash-sub {
    margin-top: 8px;
    font-size: 28px;
    letter-spacing: 0.32em;
    color: #E2E8F0;
    text-transform: uppercase;
    opacity: 0.95;
  }
`;

function ensureStyles(): void {
  if (document.getElementById('an-splash-style')) return;
  const s = document.createElement('style');
  s.id = 'an-splash-style';
  s.textContent = STYLES;
  document.head.appendChild(s);
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export interface RoundSplashHandle {
  show: (title: string, subtitle?: string) => void;
  dispose: () => void;
}

export function createRoundSplash(mount: HTMLElement): RoundSplashHandle {
  ensureStyles();
  let active: HTMLDivElement | null = null;
  let timer: number | null = null;

  function show(title: string, subtitle?: string): void {
    if (active) active.remove();
    if (timer !== null) clearTimeout(timer);
    const el = document.createElement('div');
    el.className = 'an-splash';
    el.setAttribute('data-overlay', 'round-splash');
    el.innerHTML = `
      <div class="an-splash-title">${escapeHtml(title)}</div>
      ${subtitle ? `<div class="an-splash-sub">${escapeHtml(subtitle)}</div>` : ''}
    `;
    mount.appendChild(el);
    active = el;
    timer = window.setTimeout(() => {
      el.remove();
      if (active === el) active = null;
      timer = null;
    }, 1700);
  }

  return {
    show,
    dispose() {
      if (timer !== null) clearTimeout(timer);
      if (active) active.remove();
      active = null;
    },
  };
}
